import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { loadAllClubs } from '../lib/clubStore';
import { loadInterestRegistrations } from '../lib/interestStore';
import {
  CLUB_STATUS,
  STATUS_META,
  groupClubsByStatus,
  aggregateInterestByClub,
} from '../lib/clubStatus';
import PortalHeader from '../components/PortalHeader';
import PageHeader from '../components/PageHeader';

/** Compact, data-derived signal line for a single club. */
function ClubSignals({ status, summary }) {
  const parts = [];
  if (status === CLUB_STATUS.ACTIVE) {
    parts.push(`${summary.seasonCount} ${summary.seasonCount === 1 ? 'season' : 'seasons'}`);
    if (summary.classes > 0) {
      parts.push(`${summary.classes} ${summary.classes === 1 ? 'class' : 'classes'}`);
    }
    if (summary.published > 0) parts.push(`${summary.published} published`);
    if (summary.interest > 0) parts.push(`${summary.interest} interested`);
  } else if (status === CLUB_STATUS.EARLY) {
    parts.push(`${summary.interest} interested`);
  }
  if (parts.length === 0) return null;
  return <p className="club-signals">{parts.join(' · ')}</p>;
}

function ClubCard({ entry }) {
  const { club, status, summary } = entry;
  return (
    <Link to={`/clubs/${club.id}`} className="nav-card">
      <h2>{club.name || club.id}</h2>
      <p><code>{club.id}</code></p>
      <ClubSignals status={status} summary={summary} />
    </Link>
  );
}

export default function AdminHome() {
  const [clubs, setClubs] = useState([]);
  const [seasonMap, setSeasonMap] = useState({});
  const [interestByClub, setInterestByClub] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { clubs: allClubs, seasonMap: sm } = await loadAllClubs();
        if (cancelled) return;
        setClubs(allClubs.filter((c) => c.deleted !== true));
        setSeasonMap(sm);
        // Interest registrations are a best-effort signal for early-interest
        // clubs — never let a failure here blank the whole list.
        try {
          const regs = await loadInterestRegistrations();
          if (!cancelled) setInterestByClub(aggregateInterestByClub(regs));
        } catch {
          /* fall back to interest_count on the club doc */
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const groups = useMemo(
    () => groupClubsByStatus(clubs, seasonMap, interestByClub),
    [clubs, seasonMap, interestByClub],
  );

  return (
    <>
      <PortalHeader />
      <div className="page">
        <PageHeader backTo="/" title="Global Admin" />

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ marginBottom: '0.75rem' }}>Admin Tools</h2>
        <nav className="dashboard-nav">
          <Link to="/admin/clubs" className="nav-card">
            <h2><span className="nav-icon">🏠</span> Clubs</h2>
            <p>Create, edit, and delete clubs</p>
          </Link>
          <Link to="/admin/racetimes" className="nav-card">
            <h2><span className="nav-icon">⏱</span> Racetimes</h2>
            <p>Variant editor, rotation builder, and race schedule publishing</p>
          </Link>
          <Link to="/admin/features" className="nav-card">
            <h2><span className="nav-icon">🧩</span> Feature Matrix</h2>
            <p>Capability matrix — tiers, gating, and build status</p>
          </Link>
          <Link to="/admin/gating-model" className="nav-card">
            <h2><span className="nav-icon">🔀</span> Feature Switches</h2>
            <p>Gating model, build configs, capability matrix, access overrides</p>
          </Link>
          <Link to="/admin/config" className="nav-card">
            <h2><span className="nav-icon">⚙️</span> Config Levers</h2>
            <p>Build axes, Firebase projects, secrets, remote config, observability</p>
          </Link>
          <Link to="/admin/event-index" className="nav-card">
            <h2><span className="nav-icon">🗂</span> Event Index</h2>
            <p>Global cross-club event index the app browses — status and rebuild</p>
          </Link>
          <Link to="/admin/release-checklist" className="nav-card">
            <h2><span className="nav-icon">🚀</span> Release Checklist</h2>
            <p>Step-by-step release process with stateful tickboxes</p>
          </Link>
          <Link to="/admin/diagnostics" className="nav-card">
            <h2><span className="nav-icon">🩺</span> Diagnostics</h2>
            <p>Build SHA, env, session claims — and race context when reached via /join?r=</p>
          </Link>
          <Link to="/admin/activity" className="nav-card">
            <h2><span className="nav-icon">📡</span> Live Activity</h2>
            <p>Active sessions, devices online, courses in use across the fleet</p>
          </Link>
          <Link to="/admin/beta" className="nav-card">
            <h2><span className="nav-icon">🧪</span> Beta Requests</h2>
            <p>Grant beta access, manage testers, and review requests</p>
          </Link>
          <Link to="/admin/interest" className="nav-card">
            <h2><span className="nav-icon">📋</span> Interest Registrations</h2>
            <p>Interest tracking and count recalculation</p>
          </Link>
          <Link to="/admin/products" className="nav-card">
            <h2><span className="nav-icon">💳</span> Products &amp; Entitlements</h2>
            <p>Product/tier config and entitlement mappings</p>
          </Link>
        </nav>
      </section>

      <section>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
          <h2 style={{ margin: 0 }}>Clubs</h2>
          {!loading && !error && clubs.length > 0 && (
            <Link to="/admin/clubs" className="btn btn-sm">Manage clubs</Link>
          )}
        </div>
        {loading && <p>Loading clubs...</p>}
        {error && <div className="error-banner">{error}</div>}
        {!loading && !error && clubs.length === 0 && (
          <p className="empty-state">No clubs found.</p>
        )}

        {!loading && !error && clubs.length > 0 && (
          <>
            {[CLUB_STATUS.ACTIVE, CLUB_STATUS.EARLY].map((status) => {
              const entries = groups[status];
              if (entries.length === 0) return null;
              const meta = STATUS_META[status];
              return (
                <div key={status} className="club-status-group">
                  <div className="club-status-head">
                    <span className={`status-badge club-status-badge ${status}`}>{meta.label}</span>
                    <span className="section-count">{entries.length}</span>
                    <span className="club-status-hint">{meta.hint}</span>
                  </div>
                  <nav className="dashboard-nav">
                    {entries.map((entry) => (
                      <ClubCard key={entry.club.id} entry={entry} />
                    ))}
                  </nav>
                </div>
              );
            })}

            {groups[CLUB_STATUS.INACTIVE].length > 0 && (
              <div className="club-status-group">
                <button
                  type="button"
                  className="section-toggle club-status-head"
                  onClick={() => setShowInactive((v) => !v)}
                >
                  <span className="section-toggle-arrow">{showInactive ? '▾' : '▸'}</span>
                  <span className={`status-badge club-status-badge ${CLUB_STATUS.INACTIVE}`}>
                    {STATUS_META[CLUB_STATUS.INACTIVE].label}
                  </span>
                  <span className="section-count">{groups[CLUB_STATUS.INACTIVE].length}</span>
                  <span className="club-status-hint">{STATUS_META[CLUB_STATUS.INACTIVE].hint}</span>
                </button>
                {showInactive && (
                  <ul className="club-inactive-list">
                    {groups[CLUB_STATUS.INACTIVE].map((entry) => (
                      <li key={entry.club.id}>
                        <Link to={`/clubs/${entry.club.id}`}>
                          {entry.club.name || entry.club.id}
                          <code>{entry.club.id}</code>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </section>
      </div>
    </>
  );
}
